<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { Upload } from 'lucide-vue-next'
import { normalizeImageForUpload, toJpegFileName } from '../../utils/imageOps'
import { ACCEPT_IMAGE_INPUT, isAcceptedImageUploadFile, MAX_IMAGE_UPLOAD_MB } from '../../utils/fileAccept'
import { decodeMobileUploadPayload } from '../../utils/mobileUploadPayload'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { detectCurrentUploadDeviceInfo } from '../../utils/deviceInfo'

const props = defineProps<{
  payload?: string | null
}>()

const rawPayload = computed(() => {
  const direct = String(props.payload || '').trim()
  if (direct) return direct
  if (typeof window === 'undefined') return ''
  const url = new URL(window.location.href)
  return String(url.searchParams.get('data') || url.searchParams.get('payload') || '').trim()
})
const payload = computed(() => decodeMobileUploadPayload(rawPayload.value))

type UploadStatus = 'ready' | 'uploading' | 'done' | 'error'
type UploadItem = {
  id: string
  file: File
  name: string
  previewUrl: string
  status: UploadStatus
  error?: string
}

const items = ref<UploadItem[]>([])
const dzOver = ref(false)
const uploading = ref(false)
const message = ref('')
const error = ref('')
const categoryName = ref('')

function setFiles(next: File[]) {
  const accepted = next.filter((file) => isAcceptedImageUploadFile(file))
  for (const item of items.value) URL.revokeObjectURL(item.previewUrl)
  items.value = accepted.map((file) => ({
    id: uuidv4(),
    file,
    name: file.name || 'Bild',
    previewUrl: URL.createObjectURL(file),
    status: 'ready',
  }))
  if (accepted.length !== next.length) {
    error.value = `Einige Dateien wurden übersprungen. Erlaubt sind nur Bilder bis ${MAX_IMAGE_UPLOAD_MB} MB.`
  }
}

function onPick(e: Event) {
  const input = e.target as HTMLInputElement
  const next = input.files ? Array.from(input.files) : []
  input.value = ''
  setFiles(next)
}

function onDrop(e: DragEvent) {
  dzOver.value = false
  const next = Array.from(e.dataTransfer?.files || [])
  setFiles(next)
}

const uploadUrl = computed(() => {
  const cfg = getWidgetConfig() as any
  const base = String(cfg?.editorBaseUrl || 'https://www.pixelfoto-express.de/editor').replace(/\/+$/, '')
  return `${base}/upload`
})

async function uploadOne(file: File) {
  if (!payload.value) throw new Error('Upload-Payload fehlt.')
  const device = detectCurrentUploadDeviceInfo()

  const prepared = await normalizeImageForUpload(file, file.name || 'bild.jpg', { quality: 0.92 })
  const dims = { width: prepared.width, height: prepared.height }
  const uploadFile = new File([prepared.blob], prepared.normalized ? toJpegFileName(file.name || 'bild.jpg') : String(file.name || 'bild.heic'), { type: prepared.mime })
  const fd = new FormData()
  fd.append('file', uploadFile, uploadFile.name)
  fd.append('projectCode', payload.value.projectCode)
      fd.append('meta', JSON.stringify({
        id: uuidv4(),
        name: uploadFile.name || 'Bild',
        projectCode: payload.value.projectCode,
        meta: {
          source: 'phone',
          deviceClass: device.deviceClass,
          deviceOs: device.deviceOs,
          deviceBrowser: device.deviceBrowser,
        },
        categoryId: Number(payload.value.categoryId || 0),
        ...(payload.value.productId ? { productId: payload.value.productId } : {}),
        width: dims.width || null,
        height: dims.height || null,
  }))

  const res = await fetch(uploadUrl.value, { method: 'POST', body: fd })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(txt || `Upload fehlgeschlagen (${res.status})`)
  }
}

async function loadCategoryName() {
  categoryName.value = ''
  if (!payload.value?.categoryId) return
  const cfg = getWidgetConfig() as any
  const endpoint = String(cfg?.graphqlUrl || '').trim()
  if (!endpoint) return

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(cfg?.authToken ? { Authorization: `Bearer ${cfg.authToken}` } : {}),
      },
      body: JSON.stringify({
        query: 'query MobileUploadCategory($id: ID!) { category(id: $id) { title _id } }',
        variables: { id: `/api/categories/${payload.value.categoryId}` },
      }),
    })
    const json = await res.json().catch(() => null)
    categoryName.value = String(json?.data?.category?.title || '').trim()
  } catch {
    categoryName.value = ''
  }
}

async function submit() {
  if (!payload.value) {
    error.value = 'Ungültiger Upload-Link.'
    return
  }
  if (!items.value.length) {
    error.value = 'Bitte Bilder auswählen.'
    return
  }

  uploading.value = true
  error.value = ''
  message.value = ''
  try {
    for (const item of items.value) {
      item.status = 'uploading'
      item.error = ''
      try {
        await uploadOne(item.file)
        item.status = 'done'
      } catch (e: any) {
        item.status = 'error'
        item.error = e?.message || 'Upload fehlgeschlagen.'
      }
    }
    const doneCount = items.value.filter((item) => item.status === 'done').length
    const errorCount = items.value.filter((item) => item.status === 'error').length
    if (doneCount) message.value = `${doneCount} Bild(er) wurden an Ihr Projekt gesendet.`
    if (errorCount) error.value = `${errorCount} Bild(er) konnten nicht hochgeladen werden.`
  } catch (e: any) {
    error.value = e?.message || 'Upload fehlgeschlagen.'
  } finally {
    uploading.value = false
  }
}

watch(payload, () => {
  void loadCategoryName()
}, { immediate: true })

onBeforeUnmount(() => {
  for (const item of items.value) URL.revokeObjectURL(item.previewUrl)
})
</script>

<template>
  <section class="mu">
    <div class="panel">
      <svg class="brand-logo" viewBox="0 0 198.87 50" aria-hidden="true">
        <use href="#brand" />
      </svg>
      <h1>Bilder vom Handy senden</h1>
      <p class="lead">
        Bilder werden direkt in das offene Projekt geladen und erscheinen im Upload-Drawer automatisch.
      </p>

      <div v-if="payload" class="meta">
        <span>Projekt <b>{{ payload.projectCode }}</b></span>
        <span v-if="payload.categoryId">Kategorie <b>{{ categoryName || `#${payload.categoryId}` }}</b></span>
        <span v-else>Alle Kategorien <b>freie Zuordnung im Editor</b></span>
      </div>
      <div v-else class="invalid">
        Ungültiger oder beschädigter Link.
      </div>

      <div
        class="drop"
        :class="{ over: dzOver, disabled: !payload }"
        @dragenter.prevent="dzOver = true"
        @dragover.prevent="dzOver = true"
        @dragleave.prevent="dzOver = false"
        @drop.prevent="onDrop"
      >
        <label class="drop-inner">
          <input type="file" multiple :accept="ACCEPT_IMAGE_INPUT" @change="onPick" :disabled="!payload || uploading" />
          <div class="drop-icon" aria-hidden="true">
            <Upload :size="30" />
          </div>
          <strong>Bilder auswählen</strong>
          <span>JPG, PNG, WebP, TIFF</span>
          <small>{{ items.length ? `${items.length} Datei(en) ausgewählt` : 'Dateien antippen oder hierher ziehen' }}</small>
        </label>
      </div>

      <div v-if="items.length" class="thumb-grid">
        <article v-for="item in items" :key="item.id" class="thumb-card" :data-status="item.status">
          <div class="thumb-media">
            <img :src="item.previewUrl" :alt="item.name" />
            <div v-if="item.status === 'uploading'" class="thumb-overlay">
              <span class="spinner" />
            </div>
            <div v-else-if="item.status === 'done'" class="thumb-badge thumb-badge--ok">Fertig</div>
            <div v-else-if="item.status === 'error'" class="thumb-badge thumb-badge--err">Fehler</div>
          </div>
          <div class="thumb-name">{{ item.name }}</div>
          <div v-if="item.error" class="thumb-error">{{ item.error }}</div>
        </article>
      </div>

      <button class="submit" type="button" :disabled="!payload || !items.length || uploading" @click="submit">
        {{ uploading ? 'Lädt hoch…' : 'Jetzt hochladen' }}
      </button>

      <p v-if="message" class="ok">{{ message }}</p>
      <p v-if="error" class="err">{{ error }}</p>
      <svg width="0" height="0" aria-hidden="true" style="position:absolute">
        <symbol xmlns="http://www.w3.org/2000/svg" viewBox="0 0 198.87 50" id="brand">
          <g id="Gruppe_36" transform="translate(236 239)">
            <path id="Pfad_22" d="M103.38,13.49h3.69v14h7.17v-14h4V8.34h-4c.08-3,.77-3.52,4.58-3.52h1.91V.12C119.18,0,118.44,0,117.11,0c-4.78,0-6.6.4-8.14,1.82s-1.78,2.43-1.9,6.52h-3.69Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_23" d="M133.14,13.24a14,14,0,0,1,4.66.57c1.34.53,1.74,1.46,1.74,4,0,4.05-.93,4.74-6.32,4.74a13.36,13.36,0,0,1-4.86-.61c-1.26-.53-1.62-1.38-1.62-3.89,0-4.13,1-4.86,6.4-4.86m.16-5.39c-6.72,0-9.48.44-11.26,1.74s-2.47,3.2-2.47,7.94c0,5.27.49,7.13,2.23,8.55S126,28,133.14,28s9.52-.41,11.34-1.9,2.23-3.24,2.23-8.55c0-4.78-.57-6.52-2.47-7.94s-4.58-1.74-10.94-1.74" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_24" d="M151.52,3.3v5h-3.77v5.14h3.77v8.47c.08,2.19.49,3.24,1.5,4.25,1.42,1.38,3.28,1.78,7.86,1.78,4.94,0,7.13-.61,8.59-2.43,1.09-1.42,1.38-2.59,1.46-6.24H164.4c0,2,0,2.15-.28,2.59-.32.69-1.14,1-2.59,1s-2.11-.28-2.47-.93c-.32-.49-.36-.77-.36-2.88V13.49H170V8.34H158.69v-5Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_25" d="M185.86,13.24a14,14,0,0,1,4.66.57c1.34.53,1.74,1.46,1.74,4,0,4.05-.93,4.74-6.32,4.74a13.36,13.36,0,0,1-4.86-.61c-1.26-.53-1.62-1.38-1.62-3.89,0-4.13,1-4.86,6.4-4.86M186,7.86c-6.72,0-9.48.44-11.26,1.74s-2.47,3.2-2.47,7.94c0,5.27.49,7.13,2.23,8.55s4.21,1.9,11.34,1.9,9.52-.41,11.34-1.9,2.23-3.24,2.23-8.55c0-4.78-.57-6.52-2.47-7.94S192.39,7.86,186,7.86" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_26" d="M14.22,13.24c5.23,0,6.2.69,6.2,4.29,0,2.59-.24,3.6-1.09,4.21s-2.47.85-5.23.85c-5.31,0-6.36-.73-6.36-4.42,0-4.29.85-4.94,6.48-4.94M.57,35.48H7.74V25h.32c1.42,2.27,3.24,3,8.1,3,9.56,0,11.42-1.62,11.42-10,0-4.86-.45-6.68-2-8.06C24,8.43,21.43,7.86,16.44,7.86,11,7.86,9,8.59,7.7,10.94H7.33V8.34H.57Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <path id="Pfad_27" d="M30.28,5.87h7.17V.48H30.28Zm0,21.63h7.17V8.34H30.28Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <path id="Pfad_28" d="M73.16,15.55c.08-2.67,1.21-3.28,6-3.28,2.84,0,4.29.28,4.94,1,.45.45.53.81.57,2.31Zm11.46,5.83c-.28,1.74-1.13,2-5.71,2-2.59,0-4.09-.24-4.86-.81s-.89-1.18-.89-3.12H91.87V17.26c0-4.05-.69-6.28-2.35-7.66s-4-1.82-10.49-1.82c-6.24,0-8.63.41-10.49,1.7C66.56,10.94,66,12.84,66,18c0,4.86.57,6.76,2.43,8.18S72.8,28,78.39,28c10.21,0,12.84-1.34,13.37-6.6Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <rect id="Rechteck_1" width="7.17" height="27.02" transform="translate(-141.98 -238.51)" fill="#575756"></rect>
            <path id="Pfad_29" d="M177.43,36.26c4.22,0,4.94.61,4.94,4.08,0,1.75-.3,2.67-1,3.21s-1.78.64-4.44.64c-4,0-4.81-.64-4.81-3.92,0-1.87.3-2.83,1-3.37s1.71-.64,4.31-.64M184,30.31h-1.7v5.9h-.11c-.73-1.12-1.71-1.41-4.62-1.41-3.17,0-4.47.16-5.33.66-1.35.77-1.94,2.26-1.94,4.81s.5,3.78,1.75,4.6c.93.59,2.16.77,5.1.77,3.39,0,4.28-.25,5.08-1.48h.09V45.5H184Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <path id="Pfad_30" d="M187.72,39.18c.18-2.35,1.07-2.89,4.72-2.89,4.1,0,5.13.57,5.15,2.89Zm9.93,3.08c-.07.68-.11.87-.39,1.14-.55.61-1.48.8-3.87.8-3.17,0-4.31-.2-4.94-.86a4,4,0,0,1-.77-2.71h11.76c0-2.51-.27-3.46-1.07-4.4-1-1.09-2.23-1.39-5.88-1.39-2.55,0-3.76.16-4.63.64-1.37.75-2,2.23-2,4.81,0,2.16.5,3.62,1.5,4.4s2.55,1,6.2,1c4.28,0,5.76-.87,5.83-3.39Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <path id="Pfad_31" d="M59.34,38.79c0-1.5.68-1.85,3.39-1.85,1.6,0,2.42.16,2.78.55s.3.45.32,1.3Zm6.45,3.28c-.16,1-.64,1.14-3.21,1.14a5,5,0,0,1-2.73-.45c-.39-.3-.5-.66-.5-1.75H69.86V39.78c0-2.28-.39-3.53-1.32-4.31s-2.26-1-5.9-1-4.85.23-5.9,1-1.46,1.87-1.46,4.79c0,2.73.32,3.8,1.37,4.6s2.46,1,5.6,1c5.74,0,7.22-.75,7.52-3.71Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_32" d="M95.11,37.49c2.94,0,3.49.39,3.49,2.41,0,1.46-.14,2-.61,2.37a5.67,5.67,0,0,1-2.94.48c-3,0-3.58-.41-3.58-2.48,0-2.41.48-2.78,3.65-2.78M87.43,50h4V44.12h.18c.8,1.28,1.82,1.66,4.56,1.66,5.38,0,6.42-.91,6.42-5.6,0-2.73-.25-3.76-1.12-4.53s-2.35-1.18-5.15-1.18c-3.05,0-4.22.41-4.92,1.73h-.21V34.73h-3.8Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_33" d="M104.37,34.73V45.51h4V41c0-1.66.11-2.25.5-2.78a2.54,2.54,0,0,1,2.05-.77c1.66,0,2.12.43,2.12,2h3.81V38.7c0-3.21-1.12-4.31-4.35-4.31-2.26,0-3.39.52-4.15,1.89h-.2V34.73Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_34" d="M121.91,38.79c0-1.5.68-1.85,3.39-1.85,1.6,0,2.41.16,2.78.55s.3.45.32,1.3Zm6.45,3.28c-.16,1-.64,1.14-3.21,1.14a5,5,0,0,1-2.73-.45c-.39-.3-.5-.66-.5-1.75h10.53V39.78c0-2.28-.39-3.53-1.32-4.31s-2.26-1-5.9-1-4.85.23-5.9,1-1.46,1.87-1.46,4.79c0,2.73.32,3.8,1.37,4.6s2.46,1,5.61,1c5.74,0,7.22-.75,7.52-3.71Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_35" d="M133.85,42.07a3.2,3.2,0,0,0,1,2.6c1,.89,2.3,1.12,6.22,1.12,6.24,0,7.7-.71,7.7-3.78,0-2.42-1.18-3.33-4.31-3.35a53.392,53.392,0,0,1-5.51-.09c-.66-.09-1-.27-1-.71,0-.68.77-.89,3.28-.89a5.74,5.74,0,0,1,2.55.32c.14.09.18.16.27.5h4.1c-.09-1.3-.3-1.87-.87-2.32-.91-.77-2.25-1-5.92-1a19.09,19.09,0,0,0-5.58.5c-1.28.48-1.87,1.48-1.87,3.17,0,2.28,1.28,3.24,4.33,3.28,4,0,5,0,5.51.07s.93.36.93.8c0,.77-.75,1-3.42,1-2.46,0-3.26-.27-3.33-1.21Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_36" d="M150.09,42.07a3.21,3.21,0,0,0,1,2.6c1,.89,2.3,1.12,6.22,1.12,6.24,0,7.7-.71,7.7-3.78,0-2.42-1.19-3.33-4.31-3.35a53.392,53.392,0,0,1-5.51-.09c-.66-.09-1-.27-1-.71,0-.68.78-.89,3.28-.89a5.74,5.74,0,0,1,2.55.32c.14.09.18.16.27.5h4.1c-.09-1.3-.3-1.87-.87-2.32-.91-.77-2.25-1-5.92-1A19.08,19.08,0,0,0,152,35c-1.27.48-1.87,1.48-1.87,3.17,0,2.28,1.28,3.24,4.33,3.28,4,0,5,0,5.51.07s.93.36.93.8c0,.77-.75,1-3.42,1-2.46,0-3.26-.27-3.33-1.21Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <path id="Pfad_37" d="M66.78,27.5l-9.14-9.87,8.8-9.29H56.8l-3.88,4.19L49.05,8.34H39.41l8.8,9.29L39.07,27.5H48.3l4.62-4.89,4.63,4.89Z" transform="translate(-236.57 -239)" fill="#575756"></path>
            <path id="Pfad_38" d="M86.31,45.51l-5.14-5.55,4.95-5.23H80.7l-2.18,2.36-2.18-2.36H70.92l4.94,5.23-5.13,5.55h5.19l2.6-2.75,2.6,2.75Z" transform="translate(-236.57 -239)" fill="#e30b17"></path>
            <rect id="Rechteck_2" width="2.87" height="2.15" transform="translate(-70.11 -195.73)" fill="#575756"></rect>
          </g>
        </symbol>
      </svg>
    </div>
  </section>
</template>

<style scoped>
.mu{
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 24px 16px;
  background:
    radial-gradient(circle at top, rgba(220, 38, 38, 0.08), transparent 36%),
    linear-gradient(180deg, #fff 0%, #f6f7fb 100%);
  color: #0f172a;
}
.panel{
  width: min(100%, 520px);
  margin: auto;
}
.brand-logo{
  width: 208px;
  margin: 40px auto;
  height: auto;
  display: block;
}
h1{
  margin: 8px 0 10px;
  font-size: 32px;
  line-height: 1.05;
}
.lead{
  margin: 0 0 18px;
  color: rgba(15,23,42,.68);
  font-size: 15px;
}
.meta{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
  font-size: 13px;
  color: rgba(15,23,42,.74);
}
.invalid,.err{
  color: #b91c1c;
}
.ok{
  color: #166534;
}
.drop{
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 10px;
  background: #fff;
  padding: 8px;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}
.drop.over{
  border-color: #e30b17;
  border-style: dashed;
  background: linear-gradient(180deg, rgba(227,11,23,.08), rgba(255,245,245,.98));
  box-shadow:
    inset 0 0 0 1px rgba(227,11,23,.16),
    0 0 0 3px rgba(227,11,23,.08);
}
.drop.disabled{
  opacity: .55;
  pointer-events: none;
}
.drop-inner{
  min-height: 220px;
  display: grid;
  place-items: center;
  gap: 8px;
  text-align: center;
  cursor: pointer;
  color: #0f172a;
  border-radius: 8px;
}
.drop.over .drop-inner{
  background: rgba(227,11,23,.03);
}
.drop-inner input{
  display: none;
}
.drop-icon{
  color: rgba(15,23,42,.28);
  transition: color .15s ease;
}
.drop.over .drop-icon{
  color: #e30b17;
}
.drop-inner strong{
  font-size: 18px;
}
.drop.over .drop-inner strong{
  color: #e30b17;
}
.drop-inner span{
  color: rgba(15,23,42,.66);
}
.drop.over .drop-inner span{
  color: rgba(227,11,23,.78);
}
.drop-inner small{
  color: rgba(15,23,42,.48);
}
.submit{
  width: 100%;
  margin-top: 16px;
  height: 48px;
  border: 0;
  border-radius: 3px;
  background: #dc2626;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
}
.submit:disabled{
  opacity: .5;
}
.thumb-grid{
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.thumb-card{
  display: grid;
  gap: 6px;
}
.thumb-media{
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 3px;
  overflow: hidden;
  background: #eef2f7;
  border: 1px solid rgba(15,23,42,.08);
}
.thumb-media img{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumb-overlay{
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(15,23,42,.38);
}
.spinner{
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,.34);
  border-top-color: #fff;
  animation: spin .9s linear infinite;
}
.thumb-badge{
  position: absolute;
  right: 6px;
  bottom: 6px;
  height: 22px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
}
.thumb-badge--ok{
  background: rgba(22,101,52,.92);
  color: #fff;
}
.thumb-badge--err{
  background: rgba(185,28,28,.92);
  color: #fff;
}
.thumb-name{
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.thumb-error{
  font-size: 11px;
  color: #b91c1c;
  line-height: 1.3;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
@media (max-width: 640px){
  .mu{ padding: 0; }
  .panel{
    width: 100%;
    min-height: 100vh;
    border-radius: 0;
    padding: 24px 18px 32px;
  }
  h1{ font-size: 28px; }
  .thumb-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
